import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Linkedin, Instagram, Star } from "lucide-react";
import { companyInfo, navLinks } from "../data/mockData";

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_915f609f-ac7a-45cc-b95f-47f255f32234/artifacts/wbx3meld_image.png" 
                alt="Konastone Healthcare" 
                className="h-16 w-auto bg-white rounded-lg p-1"
              />
            </div>
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              {companyInfo.tagline}
            </p>
            <div className="flex items-center gap-1 text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-current" />
              ))}
              <span className="ml-2 text-white/80 text-sm">5.0 Google Rating</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <span className="text-white/80 text-sm">{companyInfo.address}</span>
              </li>
              <li>
                <a
                  href={`tel:${companyInfo.phone}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5 text-secondary" />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5 text-secondary" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div className="text-white/80 text-sm">
                  <p>{companyInfo.hours.weekdays}</p>
                  <p>{companyInfo.hours.weekends}</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Social & CTA */}
          <div>
            <h4 className="font-serif font-semibold text-lg mb-6">Follow Us</h4>
            <div className="flex gap-4 mb-8">
              <a
                href={companyInfo.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.socialMedia.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <p className="text-sm font-medium mb-3">Need Care Support?</p>
              <Link
                to="/contact"
                data-testid="footer-cta"
                className="inline-block bg-accent hover:bg-accent/90 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-all hover:scale-105"
              >
                Book Free Assessment
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <p className="text-white/60 text-sm text-center">
            © {new Date().getFullYear()} Konastone Healthcare Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
